import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Ledger from "@/pages/Ledger";
import Parties from "@/pages/Parties";
import Categories from "@/pages/Categories";
import Reports from "@/pages/Reports";
import Users from "@/pages/Users";
import BusinessSettings from "@/pages/BusinessSettings";
import AdminBusinesses from "@/pages/AdminBusinesses";
import AuditLog from "@/pages/AuditLog";
import AcceptInvite from "@/pages/AcceptInvite";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground" data-testid="auth-loading">
        Authenticating…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { isAdmin, user } = useAuth();
  if (user === null) return null;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppOwnerOnly({ children }) {
  const { isAppOwner, user } = useAuth();
  if (user === null) return null;
  if (!isAppOwner) return <Navigate to="/dashboard" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, isAppOwner } = useAuth();
  const { pathname } = window.location;
  if (user === null) return null;
  // The application owner can still reach /signup while authenticated (to onboard new workspaces)
  if (user && pathname === "/signup" && isAppOwner) return children;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />
          <Route path="/invite/:token" element={<AcceptInvite />} />
          <Route element={<Protected><Layout /></Protected>}>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/ledger" element={<Ledger />} />
            <Route path="/parties" element={<Parties />} />
            <Route path="/categories" element={<Categories />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/users" element={<AdminOnly><Users /></AdminOnly>} />
            <Route path="/business" element={<AdminOnly><BusinessSettings /></AdminOnly>} />
            <Route path="/audit-log" element={<AdminOnly><AuditLog /></AdminOnly>} />
            <Route path="/admin/businesses" element={<AppOwnerOnly><AdminBusinesses /></AppOwnerOnly>} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
